/**
 * Authentication Types
 */

import type { User as SupabaseUser } from '@supabase/supabase-js';

export type UserRole = 'admin' | 'teacher' | 'student' | null;

export interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  verified: boolean;
  role: UserRole;
  /** True once pm_profiles has a first + last name (used to decide onboarding vs dashboard) */
  profileComplete: boolean;
  /** Set for email/password accounts created after legal acceptance was introduced. */
  requiresLegalAcceptance: boolean;
  /** Timestamp of the combined Terms of Use and Privacy Policy acceptance. */
  legalAcceptedAt?: string;
  created: string;
  updated: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export type OAuthProvider = 'google' | 'github' | 'discord' | 'apple';

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  loginWithOAuth: (provider: OAuthProvider) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
}

export interface ProfileRow {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  legal_accepted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Convert Supabase user + profile row to our User type
 */
export function toUser(
  authUser: SupabaseUser | null | undefined,
  profile?: ProfileRow | null
): User | null {
  if (!authUser) return null;

  const role: UserRole =
    profile?.role === 'admin'
      ? 'admin'
      : profile?.role === 'teacher'
        ? 'teacher'
        : profile?.role === 'student'
          ? 'student'
          : null;

  const meta = authUser.user_metadata as Record<string, unknown> | undefined;

  // Prefer our own pm_profiles columns; fall back to whatever the auth
  // provider (email signup metadata, or Google's OIDC claims) supplied.
  const firstName =
    profile?.first_name ??
    (meta?.first_name as string | undefined) ??
    (meta?.given_name as string | undefined);
  const lastName =
    profile?.last_name ??
    (meta?.last_name as string | undefined) ??
    (meta?.family_name as string | undefined);
  const name =
    profile?.name ||
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    (meta?.name as string | undefined) ||
    (meta?.full_name as string | undefined) ||
    undefined;

  return {
    id: authUser.id,
    email: authUser.email ?? '',
    name,
    firstName,
    lastName,
    avatar: meta?.avatar_url as string | undefined,
    verified: !!authUser.email_confirmed_at,
    role,
    profileComplete: !!(profile?.first_name && profile?.last_name),
    requiresLegalAcceptance: meta?.requires_legal_acceptance === true,
    legalAcceptedAt: profile?.legal_accepted_at ?? undefined,
    created: profile?.created_at ?? authUser.created_at ?? '',
    updated: profile?.updated_at ?? authUser.updated_at ?? '',
  };
}
